import { ButtonWidget } from '@tableau/widgets';
import * as React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Messages } from '~src/codegen/Localize';
import { ClearSavedPasswordsRequest } from '~src/ts/ServerApi/requests/ClearSavedPasswordsRequest';
import { UpdateBooleanServerSettingNames } from '~src/ts/ServerApi/requests/UpdateBooleanServerSettingRequest';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { ServerSettingsInstance } from '../../../../services/ServerSettings';
import { SettingsStyles } from '../../../../styles/CommonStyles';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import settingsGroupDescription = SettingsStyles.settingsGroupDescription;

export const SAVED_PASSWORD_FIELD = 'settings.savedPasswordsEnabled';
export const SAVED_ACCESS_TOKENS_ENABLED_FIELD = 'settings.savedAccessTokensEnabled';

export const SAVED_PASSWORD_TEST_ID = 'saved-passwords-enabled';
export const SAVED_ACCESS_TOKENS_ENABLED_TEST_ID = 'saved-access-tokens-enabled';
export const CLEAR_SAVED_CREDENTIALS_TEST_ID = 'clear-saved-credentials';

export type SavedCredentialsSectionProps = ServerSettingsProps<'savedPasswordsEnabled' | 'savedAccessTokensEnabled'>;

export function SaveCredentialsSection(props: SavedCredentialsSectionProps) {
  return (
    <SettingsSection
      title={Messages.savedCredentials()}
    >
      {renderPasswordCheckbox()}
      {renderAccessTokenCheckbox()}
      {renderClearButton()}
    </SettingsSection>
  );
}

function renderPasswordCheckbox() {
  return (
    <div className={settingsGroupDescription}>
      <div className={settingsGroupDescription}>{Messages.savedPasswordsDetails()}</div>
      <FormikCheckbox
        name={SAVED_PASSWORD_FIELD}
        label={Messages.savedPasswordsControlLabel()}
        testId={SAVED_PASSWORD_TEST_ID}
      />
    </div>
  );
}

function renderAccessTokenCheckbox() {
  return (
    <div className={settingsGroupDescription}>
      <div className={settingsGroupDescription}>{Messages.savedAccessTokensDetails()}</div>
      <FormikCheckbox
        name={SAVED_ACCESS_TOKENS_ENABLED_FIELD}
        label={Messages.savedAccessTokensControlLabel()}
        testId={SAVED_ACCESS_TOKENS_ENABLED_TEST_ID}
      />
    </div>
  );
}

function renderClearButton() {
  return (
    <ButtonWidget
      testId={CLEAR_SAVED_CREDENTIALS_TEST_ID}
      onClick={clearSavedCredentials}
    >
      {Messages.clearSavedCredentials_action()}
    </ButtonWidget>
  );
}

async function clearSavedCredentials() {
  await openReactConfirmationDialog({
    title: Messages.clearSavedCredentials(),
    message: Messages.clearServerSavedCredentialsConfirmMessage(),
    actionName: Messages.clearSavedCredentialsConfirmButton(),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: null,
    action: async () => {
      try {
        await WebHttpClient.sendRequest(new ClearSavedPasswordsRequest());
        ToasterService.instance.pop({type: ToastType.Info, text: Messages.clearSavedCredentialsSuccess()});
      } catch (error) {
        ToasterService.instance.pop({type: ToastType.Error, text: Messages.clearSavedCredentialsError()});
      }
    }
  }).result;
}

export const SavedCredentialsSpecs = componentRegistryEntry({
  name: 'SaveCredentialsSection',
  component: SaveCredentialsSection,
  onSubmit: async settings => {
    await Promise.all([
      ServerSettingsInstance.updateBoolean(UpdateBooleanServerSettingNames.savedPasswordsEnabled, settings.savedPasswordsEnabled),
      ServerSettingsInstance.updateBoolean(UpdateBooleanServerSettingNames.savedAccessTokensEnabled, settings.savedAccessTokensEnabled),
    ]);
  }
});
